/**
 * Project Tabs Component (Simplified)
 * Light QA Contract Compliant:
 * - Renders ProjectList directly with Add Project button
 * - No tabs / no meetings toggle (moved to /meetings route)
 */

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Plus } from 'lucide-react';
import { useConsolidatedPermissions } from '@/hooks/useConsolidatedPermissions';
import { useAuth } from '@/hooks/useAuth';
import { useMutationWithToast } from '@/hooks/useQueryWithToast';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { ProjectAddDialog } from '@/components/projects/ProjectAddDialog';
import type { ProjectStatus } from '@/types/project';
import ProjectList from './ProjectList';

const ProjectTabs = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const consolidatedPerms = useConsolidatedPermissions();
  const permissions = consolidatedPerms.data;
  const [showAddDialog, setShowAddDialog] = useState(false);

  // Add project mutation
  const addProjectMutation = useMutationWithToast({
    mutationFn: async (projectData: { 
      project_name: string; 
      image_file: File | null;
      status?: ProjectStatus;
      project_type?: string;
      project_group?: string;
      contract_year?: string;
      company?: string;
    }) => {
      if (!user?.id) {
        throw new Error('User must be authenticated to create projects');
      }

      let imageUrl = null;
      
      if (projectData.image_file) {
        const formData = new FormData();
        formData.append('file', projectData.image_file);
        
        const { data: uploadResponse, error: uploadError } = await supabase.functions.invoke('upload-project-image', {
          body: formData
        });
        
        if (uploadError) {
          throw new Error(`Failed to upload image: ${uploadError.message}`);
        }
        
        imageUrl = uploadResponse.imageUrl;
      }
      
      const { data, error } = await supabase
        .from('projects')
        .insert({
          project_name: projectData.project_name,
          image_url: imageUrl,
          user_id: user.id,
          status: projectData.status || 'tender',
          project_type: projectData.project_type || 'infrastructure',
          project_group: projectData.project_group || null,
          contract_year: projectData.contract_year || null,
          company: projectData.company || null
        })
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      setShowAddDialog(false);
      queryClient.invalidateQueries({ queryKey: ['projects-optimized'] });
      queryClient.invalidateQueries({ queryKey: ['projects-count'] });
      queryClient.invalidateQueries({ queryKey: ['global-project-access'] });
      queryClient.refetchQueries({ queryKey: ['global-project-access'], type: 'all' });
    },
    successMessage: "Đã tạo dự án mới",
    errorMessage: "Không thể tạo dự án"
  });

  return (
    <div className="w-full">
      {permissions?.canCreateProjects && (
        <div className="flex justify-end mb-4">
          <Button 
            onClick={() => setShowAddDialog(true)}
            size="sm"
            className="gap-1"
          >
            <Plus className="h-4 w-4" />
            <span className="hidden sm:inline">Thêm Dự Án</span>
            <span className="sm:hidden">Thêm</span>
          </Button>
        </div>
      )}
      <ProjectList />
      
      <ProjectAddDialog
        isOpen={showAddDialog}
        onClose={() => setShowAddDialog(false)}
        onSubmit={addProjectMutation.mutate}
        isLoading={addProjectMutation.isPending}
      />
    </div>
  );
};

export default ProjectTabs;